import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, inject, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';

import { DomSanitizer } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { EventService } from '../../services/event.service';

import { verifyLanguage } from '../../shared/constants/app-enums';

@Component({
  selector: 'app-language',
  templateUrl: './language.component.html',
  styleUrls: ['./language.component.scss'],
  standalone: false,
})
export class LanguageComponent implements AfterViewInit {
  lang!: string;
  isProduction = environment.production;
  isLoaded: boolean = false;
  isDesclaimerClosed: boolean = false;

  private readonly platformId = inject(PLATFORM_ID);
  // Use the DOCUMENT token rather than the global `document`/`window` for SSR safety
  private readonly document = inject(DOCUMENT);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly eventService = inject(EventService);

  constructor() {

    this.translate.setDefaultLang('en');
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((navEnd: NavigationEnd) => {
        let lang = navEnd.urlAfterRedirects.split('/')[1];
        lang = lang.split('#')[0];
        lang = lang.split('?')[0];

        if (lang && verifyLanguage(lang)) {
          this.lang = lang;
        } else {
          this.lang = 'en';
        }
        this.translate.use(this.lang);

        // Track page view for pixel events
        const fullUrl = navEnd.urlAfterRedirects.split('?')[0]; // Remove query params
        const hashIndex = fullUrl.indexOf('#');

        let pageName = 'home';

        // Check if there's a hash fragment (e.g., #about, #tokenomics)
        if (hashIndex !== -1) {
          const hashFragment = fullUrl.substring(hashIndex + 1);
          if (hashFragment) {
            pageName = hashFragment;
          }
        } else {
          // No hash fragment, extract from URL path
          const pagePath = fullUrl;
          // Remove leading/trailing slashes and split by '/'
          const pathParts = pagePath
            .replace(/^\/|\/$/g, '')
            .split('/')
            .filter((p: string) => p);

          // Filter out language codes to get the actual page path
          const pagePathWithoutLang = pathParts.filter((p: string) => !verifyLanguage(p));

          if (pagePathWithoutLang.length > 0) {
            // Join remaining parts with '/' for nested routes, or use the first part for simple routes
            pageName = pagePathWithoutLang.join('/');
          }
        }
      });

  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isLoaded = true;
    }
  }

}
